import axios from "axios";
import {makePersistable} from "mobx-persist-store";
import {action, computed, makeObservable, observable, runInAction} from "mobx";
import {updateUser, userInfo} from "@ui/api/auth";

export interface IUser {
	readonly isLoggedIn: boolean
  readonly emailVerified: boolean
	readonly token?: string
	readonly refreshToken?: string
	readonly email?: string
	readonly settings: {
		locale: string
		theme?: string
	}
	readonly firstname?: string
	readonly lastname?: string
	readonly instance: string
	readonly id: string
	

	readonly authenticated: boolean

	login(params: {username: string, password: string}): Promise<void>
	register(params: TRegisterProps): Promise<void>
  sendOTP({email, smartToken}: TSendOTPProps): Promise<void>
	logout(): Promise<void>
	check(): Promise<void>
  checkEmailVerified(): void
	userinfo(): Promise<void>
	refresh(): Promise<void>
	updateUser(params: {email: string, firstName?: string, lastName?: string}): Promise<void>
}

const parseJwt = (token: string): any => {
	const base64Url = token.split(".")[1]
	const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/")
	const jsonPayload = decodeURIComponent(atob(base64).split("").map(char => "%".concat("00".concat(char.charCodeAt(0).toString(16)).slice(-2))).join(""))
	return JSON.parse(jsonPayload)
}
const isEmailVerified = (token: string): boolean => {
  const tokenInfo = parseJwt(token as string)
  return tokenInfo.email_verified
}
type TRegisterProps = {
  username: string
  email: string
  password: string
  smartToken: string
}
type TSendOTPProps = {
  email: string
  smartToken: string
}

export class User implements IUser {
	isLoggedIn: boolean = false
	token: string = ""
	refreshToken: string = ""
  emailVerified: boolean = false

	id: string = ""
	email: string = ""
	settings: {locale: string, theme?: string} = {locale: navigator.language.substr(0, 2)}
	firstname: string = ""
	lastname: string = ""
	instance: string

	constructor() {
		this.instance = self.crypto.randomUUID()

		makeObservable(this, {
			isLoggedIn: observable,
			token: observable,
			refreshToken: observable,
			email: observable,
			settings: observable,
			firstname: observable,
			lastname: observable,
      emailVerified: observable,

			authenticated: computed,
			check: action,
			login: action,
			logout: action,
			refresh: action,
			userinfo: action
		})

		makePersistable(this, {
			name: 'user',
			properties: ["emailVerified", "isLoggedIn", "token", "refreshToken", "email", "settings", "firstname", "lastname", "id"],
			storage: window.localStorage
		})

		makePersistable(this, {
			name: 'user',
			properties: ["instance"],
			storage: window.sessionStorage
		})
	}

	get authenticated() {
		return this.isLoggedIn
	}

	async check(): Promise<void> {
		if (!this.token) {
			runInAction(() => this.isLoggedIn = false)
			return
		}

		await this.refresh()
		if (this.isLoggedIn) {
			await this.userinfo()
		}
	}

  checkEmailVerified(): void {
    this.emailVerified = isEmailVerified(this.token)
  }

	async register({email, username, password, smartToken}: TRegisterProps): Promise<void> {
		const instance = axios.create({
			baseURL: "",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded"
			}
		})
		try {
			const response = await instance.request({
        url: "/reg/v1/account/create",
				method: "POST",
				data: {
					email,
					username,
          password,
          "smart-token": smartToken
				}
			})
		} catch (error) {
			console.log(error)
      throw error
		}
	}

  async sendOTP({email, smartToken}: TSendOTPProps): Promise<void> {
    const instance = axios.create({
			baseURL: "",
			headers: {"Content-Type": "application/x-www-form-urlencoded"}
		})
		try {
			await instance.request({
        url: "/reg/v1/account/otp",
				method: "POST",
				data: {email, "smart-token": smartToken}
			})
		} catch (error) {
			console.log(error)
      throw error
		}
  }

	async login({username, password}: {username: string, password: string}): Promise<void> {
		const instance = axios.create({
			baseURL: "",
			headers: {"Content-Type": "application/x-www-form-urlencoded"}
		})

		try {
			const response = await instance.request({
				url: process.env.TOKEN_URL,
				method: "POST",
				data: {
					grant_type: "password",
					client_id: process.env.CLIENT_ID,
					scope: "email profile openid",
					username,
					password
				}
			})
			const {access_token, refresh_token} = response.data
			if (access_token && refresh_token) {
				runInAction(async () => {
					this.token = access_token
					this.refreshToken = refresh_token
					this.isLoggedIn = true
          this.emailVerified = isEmailVerified(this.token)
				})
        await this.userinfo()
			}
		} catch (error) {
			runInAction(() => this.isLoggedIn = false)
      throw error
		}
	}

	async logout(): Promise<void> {
		if (this.refreshToken) {
			const instance = axios.create({
				baseURL: "",
				headers: {"Content-Type": "application/x-www-form-urlencoded"}
			})

			try {
				await instance.request({
					url: process.env.LOGOUT_URL,
					method: "POST",
					data: {
						client_id: process.env.CLIENT_ID,
						refresh_token: this.refreshToken
					}
				})
				runInAction(() => {
					this.isLoggedIn = false
					this.token = ""
					this.email = ""
					this.firstname = ""
					this.lastname = ""
				})
			} catch (error) {
				console.log(error)
			}
		}
	}

	async refresh(): Promise<void> {
		if (this.refreshToken) {
			const instance = axios.create({
				baseURL: "",
				headers: {"Content-Type": "application/x-www-form-urlencoded"}
			})

			try {
				const response = await instance.request({
					url: process.env.TOKEN_URL,
					method: "POST",
					data: {
						refresh_token: this.refreshToken,
						grant_type: "refresh_token",
						client_id: process.env.CLIENT_ID,
						scope: "email profile openid",
					}
				})
				const {access_token, refresh_token} = response.data
				if (access_token && refresh_token) {
					runInAction(() => {
						this.token = access_token
						this.refreshToken = refresh_token
						this.isLoggedIn = true
					})

				}
			} catch (error) {
				console.log(error)
				runInAction(() => this.isLoggedIn = false)
			}
		}
	}

	async userinfo(): Promise<void> {
		if (!this.token) {
			runInAction(() => this.isLoggedIn = false)
			return
		}

    await userInfo()
      .then(({email, family_name, given_name, sub, email_verified = false}) => runInAction(() => {
				this.id = sub
				this.email = email
				this.firstname = given_name
				this.lastname = family_name
				this.isLoggedIn = true
        this.emailVerified = email_verified
			}))
      .catch(error => {
        console.log(error)
			  runInAction(() => this.isLoggedIn = false)
      })
	}

	async updateUser(data: {email: string, firstName?: string, lastName?: string}): Promise<void> {
    await updateUser(this.id, data)
      .then(() => runInAction(() => {
				this.email = data.email
				this.firstname = data.firstName || ""
				this.lastname = data.lastName || ""
			}))
      .catch((error) => {
        console.log(error)
        throw error
      })
	}
}
